import { Suspense, useState } from "react";
import { Link, Outlet, useNavigate, useOutletContext, useParams } from "react-router";
import styled from "styled-components";

import useCanEditBrand from "../../../../clients/users/useCanEditBrand";
import * as UI from "../../../../modules/ui-kit";
import {
    confirmUnsavedChanges,
    type UnsavedChangesContext,
} from "../../../../modules/unsaved-changes";
import Countries from "../../../products/ui/components/form/Countries";
import usePricelists from "../../clients/usePricelists";

import type { PriceListAppOutletContext } from "../../index";

const Styled = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    transition: opacity 200ms;

    &:not(:last-child):not(:hover):not(:focus-within) {
        opacity: 0.4;
    }

    ol {
        display: flex;
        flex-direction: column;
        gap: 8px;
        list-style: none;
        margin: 0;
        padding: 0;
    }

    li a {
        color: #000;
        display: block;
        font-size: 1.2rem;
        overflow: hidden;
        position: relative;
        text-decoration: none;
        text-overflow: ellipsis;
        white-space: nowrap;

        ${UI.FluidAccent2}

        &:hover {
            text-decoration: none;
        }
    }

    .meta {
        color: #666;
        font-size: 0.85rem;
    }

    li a.archived {
        color: #d00;
    }

    .deprecated-label {
        color: #d00;
        font-weight: bold;
        margin-right: 6px;
    }

    .default-label {
        color: #4dabf7;
        font-weight: bold;
        margin-right: 6px;
    }
`;

export type PricelistsOutletContext = UnsavedChangesContext;

export default function Pricelists() {
    const guard = useOutletContext<PriceListAppOutletContext>();
    const { brandID } = useParams();
    const canEdit = useCanEditBrand(brandID);
    const [filterCountries, setFilterCountries] = useState<string[]>([]);

    return (
        <>
            <Styled>
                {canEdit && (
                    <UI.A as={Link} to="./new">
                        create new pricelist
                    </UI.A>
                )}
                <div>
                    <span style={{ fontSize: "0.85rem", color: "#666" }}>Filter by country</span>
                    <Countries value={filterCountries} onChange={setFilterCountries} />
                </div>
                <Suspense fallback={<span>loading</span>}>
                    <PricelistList guard={guard} filterCountries={filterCountries} />
                </Suspense>
            </Styled>
            <Outlet context={guard satisfies PricelistsOutletContext} />
        </>
    );
}

function PricelistList({ guard, filterCountries }: { guard: UnsavedChangesContext; filterCountries: string[] }) {
    const { brandID, pricelistID } = useParams();
    const navigate = useNavigate();
    const { data } = usePricelists(brandID);

    const pricelists = data.pricelists
        .filter((p) =>
            !filterCountries.length || !p.countries || p.countries.some((c) => filterCountries.includes(c)));

    if (!data.pricelists.length) return <span>no price lists</span>;
    if (!pricelists.length) return <span>no price lists match filter</span>;

    async function handleSelect(id: string) {
        if (pricelistID === id) return;
        if (await confirmUnsavedChanges(guard)) navigate(id);
    }

    return (
        <ol>
            {pricelists.map((pricelist) => {
                const label = pricelist.name[0]?.text ?? "UNNAMED";
                const meta = [
                    pricelist.currency,
                    pricelist.countries?.join(", ") || "all regions",
                    `${pricelist.productCount} product${pricelist.productCount === 1 ? "" : "s"}`,
                ]
                    .filter(Boolean)
                    .join(" · ");

                return (
                    <li key={pricelist.id}>
                        <a
                            href="#"
                            className={pricelist.archived ? "archived" : undefined}
                            onClick={(e) => { e.preventDefault(); handleSelect(pricelist.id); }}
                        >
                            {pricelist.archived && <span className="deprecated-label">DEPRECATED</span>}
                            {(pricelist.defaultForCountries ?? []).length > 0 && <span className="default-label">DEFAULT</span>}
                            {label}
                        </a>
                        {meta && <span className="meta">{meta}</span>}
                    </li>
                );
            })}
        </ol>
    );
}
